import React, { useCallback, useState } from 'react';
import { styled } from '@this/constants/themes';
import _ from 'lodash';
import { observer } from 'mobx-react';

import A from '@this/shared/atoms/a';
import type { FilterableSelectorOption } from '@this/shared/filterable_selector/filterable_selector';

import type ReserveInfo from '@this/domain/reserve_info2';
import OrderItemMapping from '@this/domain/order_item_mapping2';
import InputCustomerInformationFilterableSelector from '../input_customer_information_filterable_selector/input_customer_information_filterable_selector';
import SelectProjects from './select_projects/select_projects';

type Props = {
  reserveInfo: ReserveInfo;
  workflowFormWidth: string;
};

const DETAIL_FORM_WIDTH = '240px';

const ProjectNameSection: React.FC<Props> = ({ reserveInfo, workflowFormWidth }) => {
  const [showDetail, setShowDetail] = useState(false);

  const onClickChangeDetailMode = useCallback(
    (mode: 'item' | 'traveler' | 'both') => {
      reserveInfo.projectDetailMode = mode;
      reserveInfo.initOrderItemMappings('project');
    },
    [reserveInfo]
  );

  const onSelectDetail = useCallback(
    (changingMapping: OrderItemMapping, option: FilterableSelectorOption | null) => {
      const values = option?.value.split('-');
      if (changingMapping && values && values?.length === 2) {
        if (values[0] === 'project') {
          changingMapping.projectId = Number(values[1]);
          changingMapping.selectedOptionLabel = option?.label || '';
        } else if (values[0] === 'share') {
          changingMapping.projectShareId = Number(values[1]);
          if (option?.labelLines) {
            changingMapping.selectedOptionLabel = option.labelLines[0] || '';
          }
        }
      } else if (option === null) {
        changingMapping.projectId = -1;
        changingMapping.selectedOptionLabel = '';
      }
    },
    []
  );

  const onSelect = useCallback(
    (option: FilterableSelectorOption | null) => {
      reserveInfo.projectId = option ? Number(option.value) : undefined;
      reserveInfo.orderItemMappingsProjectPart = [
        new OrderItemMapping({
          travelerId: -1,
          orderItemType: 'all',
          orderItemIndex: -1,
          projectId: Number(option?.value)
        })
      ];
    },
    [reserveInfo]
  );

  return (
    <>
      <Label>
        プロジェクト(案件)
        {reserveInfo.isProjectNameRequired && <Red>*</Red>}
      </Label>
      {showDetail ? (
        <ShareDetailArea workflowFormWidth={workflowFormWidth}>
          {reserveInfo.projectDetailMode === 'item' ? (
            <>
              <ShareDetailFlexArea>
                <ShareDetailModeSelected>商品ごと</ShareDetailModeSelected>
                <ShareDetailModeLinkArea>
                  <A onClick={() => onClickChangeDetailMode('traveler')}>出張者ごと</A>
                </ShareDetailModeLinkArea>
                <ShareDetailModeLinkArea>
                  <A onClick={() => onClickChangeDetailMode('both')}>商品×出張者ごと</A>
                </ShareDetailModeLinkArea>
              </ShareDetailFlexArea>
              {reserveInfo.reservingTrip.isPackage ? (
                <ShareDetailFlexArea>
                  <ShareDetailLabel>パッケージ</ShareDetailLabel>
                  <ShareDetailSelectArea>
                    <InputCustomerInformationFilterableSelector
                      placeholder="プロジェクト(案件)を検索"
                      options={reserveInfo.projectDetailOptions}
                      selected={{
                        label:
                          reserveInfo.orderItemMappingsProjectPart.find(
                            o => o.travelerId === -1 && o.orderItemType === 'package' && o.orderItemIndex === 1
                          )?.selectedOptionLabel || '',
                        value: '',
                        matcher: ''
                      }}
                      onSelect={option => {
                        const mapping = reserveInfo.orderItemMappingsProjectPart.find(
                          o => o.travelerId === -1 && o.orderItemType === 'package' && o.orderItemIndex === 1
                        );
                        if (mapping) {
                          onSelectDetail(mapping, option);
                        }
                      }}
                      isRequired={reserveInfo.isProjectNameRequired}
                      workflowFormWidth={DETAIL_FORM_WIDTH}
                    />
                  </ShareDetailSelectArea>
                </ShareDetailFlexArea>
              ) : (
                <>
                  {reserveInfo.itemList.map((item, i: number) => (
                    <div key={`item${i}`}>
                      <ShareDetailFlexArea>
                        {item?.elementType === 'hotel' ? (
                          <ShareDetailLabel>{i + 1}. ホテル</ShareDetailLabel>
                        ) : (
                          <ShareDetailLabel>{i + 1}. 経路</ShareDetailLabel>
                        )}
                        <ShareDetailSelectArea>
                          <InputCustomerInformationFilterableSelector
                            placeholder="プロジェクト(案件)を検索"
                            options={reserveInfo.projectDetailOptions}
                            selected={{
                              label:
                                reserveInfo.orderItemMappingsProjectPart.find(
                                  o =>
                                    o.travelerId === -1 &&
                                    o.orderItemType === 'item' &&
                                    o.orderItemIndex === Number(item?.index)
                                )?.selectedOptionLabel || '',
                              value: '',
                              matcher: ''
                            }}
                            onSelect={option => {
                              const mapping = reserveInfo.orderItemMappingsProjectPart.find(
                                o =>
                                  o.travelerId === -1 &&
                                  o.orderItemType === 'item' &&
                                  o.orderItemIndex === Number(item?.index)
                              );
                              if (mapping) {
                                onSelectDetail(mapping, option);
                              }
                            }}
                            isRequired={reserveInfo.isProjectNameRequired}
                            workflowFormWidth={DETAIL_FORM_WIDTH}
                          />
                        </ShareDetailSelectArea>
                      </ShareDetailFlexArea>
                    </div>
                  ))}
                </>
              )}
              {reserveInfo.rentalCarRequired === 'true' && (
                <>
                  {reserveInfo.reservingTrip.rentalCars.map((rentalCar, car_index) => (
                    <ShareDetailFlexArea key={`car${car_index}`}>
                      <ShareDetailLabel>レンタカー {car_index + 1}</ShareDetailLabel>
                      <ShareDetailSelectArea>
                        <InputCustomerInformationFilterableSelector
                          placeholder="プロジェクト(案件)を検索"
                          options={reserveInfo.projectDetailOptions}
                          selected={{
                            label:
                              reserveInfo.orderItemMappingsProjectPart.find(
                                o =>
                                  o.travelerId === -1 &&
                                  o.orderItemType === 'rental_car' &&
                                  o.orderItemIndex === car_index
                              )?.selectedOptionLabel || '',
                            value: '',
                            matcher: ''
                          }}
                          onSelect={option => {
                            const mapping = reserveInfo.orderItemMappingsProjectPart.find(
                              o =>
                                o.travelerId === -1 &&
                                o.orderItemType === 'rental_car' &&
                                o.orderItemIndex === car_index
                            );
                            if (mapping) {
                              onSelectDetail(mapping, option);
                            }
                          }}
                          isRequired={reserveInfo.isProjectNameRequired}
                          workflowFormWidth={DETAIL_FORM_WIDTH}
                        />
                      </ShareDetailSelectArea>
                    </ShareDetailFlexArea>
                  ))}
                </>
              )}
            </>
          ) : reserveInfo.projectDetailMode === 'traveler' ? (
            <>
              <ShareDetailFlexArea>
                <>
                  <ShareDetailModeLinkArea>
                    <A onClick={() => onClickChangeDetailMode('item')}>商品ごと</A>
                  </ShareDetailModeLinkArea>
                  <ShareDetailModeSelected>出張者ごと</ShareDetailModeSelected>
                  <ShareDetailModeLinkArea>
                    <A onClick={() => onClickChangeDetailMode('both')}>商品×出張者ごと</A>
                  </ShareDetailModeLinkArea>
                </>
              </ShareDetailFlexArea>
              {reserveInfo.reservingTrip.travelers.map((traveler, traveler_index) => (
                <ShareDetailFlexArea key={traveler_index}>
                  <MappingDetailLabel>{traveler.travelerName}</MappingDetailLabel>
                  <ShareDetailSelectArea>
                    <InputCustomerInformationFilterableSelector
                      placeholder="プロジェクト(案件)を検索"
                      options={reserveInfo.projectDetailOptions}
                      selected={{
                        label:
                          reserveInfo.orderItemMappingsProjectPart.find(
                            o =>
                              o.travelerId === traveler_index &&
                              o.orderItemType === 'all' &&
                              o.orderItemIndex === -1
                          )?.selectedOptionLabel || '',
                        value: '',
                        matcher: ''
                      }}
                      onSelect={option => {
                        const mapping = reserveInfo.orderItemMappingsProjectPart.find(
                          o =>
                            o.travelerId === traveler_index && o.orderItemType === 'all' && o.orderItemIndex === -1
                        );
                        if (mapping) {
                          onSelectDetail(mapping, option);
                        }
                      }}
                      isRequired={reserveInfo.isProjectNameRequired}
                      workflowFormWidth={DETAIL_FORM_WIDTH}
                    />
                  </ShareDetailSelectArea>
                </ShareDetailFlexArea>
              ))}
            </>
          ) : reserveInfo.projectDetailMode === 'both' ? (
            <>
              <ShareDetailFlexArea>
                <ShareDetailModeLinkArea>
                  <A onClick={() => onClickChangeDetailMode('item')}>商品ごと</A>
                </ShareDetailModeLinkArea>
                <ShareDetailModeLinkArea>
                  <A onClick={() => onClickChangeDetailMode('traveler')}>出張者ごと</A>
                </ShareDetailModeLinkArea>
                <ShareDetailModeSelected>商品×出張者ごと</ShareDetailModeSelected>
              </ShareDetailFlexArea>
              {reserveInfo.reservingTrip.isPackage ? (
                <>
                  <MappingDetailLabel>パッケージ</MappingDetailLabel>
                  <ShareDetailIndentedArea>
                    {reserveInfo.reservingTrip.travelers.map((traveler, traveler_index) => (
                      <ShareDetailFlexArea key={traveler_index}>
                        <MappingDetailLabel>{traveler.travelerName}</MappingDetailLabel>
                        <ShareDetailSelectArea>
                          <InputCustomerInformationFilterableSelector
                            placeholder="プロジェクト(案件)を検索"
                            options={reserveInfo.projectDetailOptions}
                            selected={{
                              label:
                                reserveInfo.orderItemMappingsProjectPart.find(
                                  o =>
                                    o.travelerId === traveler_index &&
                                    o.orderItemType === 'package' &&
                                    o.orderItemIndex === 1
                                )?.selectedOptionLabel || '',
                              value: '',
                              matcher: ''
                            }}
                            onSelect={option => {
                              const mapping = reserveInfo.orderItemMappingsProjectPart.find(
                                o =>
                                  o.travelerId === traveler_index &&
                                  o.orderItemType === 'package' &&
                                  o.orderItemIndex === 1
                              );
                              if (mapping) {
                                onSelectDetail(mapping, option);
                              }
                            }}
                            isRequired={reserveInfo.isProjectNameRequired}
                            workflowFormWidth={DETAIL_FORM_WIDTH}
                          />
                        </ShareDetailSelectArea>
                      </ShareDetailFlexArea>
                    ))}
                  </ShareDetailIndentedArea>
                </>
              ) : (
                <>
                  {reserveInfo.itemList.map((item, i: number) => (
                    <div key={`item${i}`}>
                      {item?.elementType === 'hotel' ? (
                        <MappingDetailLabel>{i + 1}. ホテル</MappingDetailLabel>
                      ) : (
                        <MappingDetailLabel>{i + 1}. 経路</MappingDetailLabel>
                      )}
                      <ShareDetailIndentedArea>
                        {reserveInfo.reservingTrip.travelers.map((traveler, traveler_index) => (
                          <ShareDetailFlexArea key={traveler_index}>
                            <MappingDetailLabel>{traveler.travelerName}</MappingDetailLabel>
                            <ShareDetailSelectArea>
                              <InputCustomerInformationFilterableSelector
                                placeholder="プロジェクト(案件)を検索"
                                options={reserveInfo.projectDetailOptions}
                                selected={{
                                  label:
                                    reserveInfo.orderItemMappingsProjectPart.find(
                                      o =>
                                        o.travelerId === traveler_index &&
                                        o.orderItemType === 'item' &&
                                        o.orderItemIndex === Number(item?.index)
                                    )?.selectedOptionLabel || '',
                                  value: '',
                                  matcher: ''
                                }}
                                onSelect={option => {
                                  const mapping = reserveInfo.orderItemMappingsProjectPart.find(
                                    o =>
                                      o.travelerId === traveler_index &&
                                      o.orderItemType === 'item' &&
                                      o.orderItemIndex === Number(item?.index)
                                  );
                                  if (mapping) {
                                    onSelectDetail(mapping, option);
                                  }
                                }}
                                isRequired={reserveInfo.isProjectNameRequired}
                                workflowFormWidth={DETAIL_FORM_WIDTH}
                              />
                            </ShareDetailSelectArea>
                          </ShareDetailFlexArea>
                        ))}
                      </ShareDetailIndentedArea>
                    </div>
                  ))}
                </>
              )}
              {reserveInfo.rentalCarRequired === 'true' && (
                <>
                  {reserveInfo.reservingTrip.rentalCars.map((rentalCar, car_index) => (
                    <div key={`car${car_index}`}>
                      <MappingDetailLabel>レンタカー {car_index + 1}</MappingDetailLabel>
                      <ShareDetailIndentedArea>
                        {reserveInfo.reservingTrip.travelers.map((traveler, traveler_index) => (
                          <ShareDetailFlexArea key={traveler_index}>
                            <MappingDetailLabel>{traveler.travelerName}</MappingDetailLabel>
                            <ShareDetailSelectArea>
                              <InputCustomerInformationFilterableSelector
                                placeholder="プロジェクト(案件)を検索"
                                options={reserveInfo.projectDetailOptions}
                                selected={{
                                  label:
                                    reserveInfo.orderItemMappingsProjectPart.find(
                                      o =>
                                        o.travelerId === traveler_index &&
                                        o.orderItemType === 'rental_car' &&
                                        o.orderItemIndex === car_index
                                    )?.selectedOptionLabel || '',
                                  value: '',
                                  matcher: ''
                                }}
                                onSelect={option => {
                                  const mapping = reserveInfo.orderItemMappingsProjectPart.find(
                                    o =>
                                      o.travelerId === traveler_index &&
                                      o.orderItemType === 'rental_car' &&
                                      o.orderItemIndex === car_index
                                  );
                                  if (mapping) {
                                    onSelectDetail(mapping, option);
                                  }
                                }}
                                isRequired={reserveInfo.isProjectNameRequired}
                                workflowFormWidth={DETAIL_FORM_WIDTH}
                              />
                            </ShareDetailSelectArea>
                          </ShareDetailFlexArea>
                        ))}
                      </ShareDetailIndentedArea>
                    </div>
                  ))}
                </>
              )}
            </>
          ) : null}
          <DetailLinkArea>
            <A
              onClick={() => {
                reserveInfo.showProjectDetail = false;
                setShowDetail(false);
              }}
            >
              キャンセル
            </A>
          </DetailLinkArea>
        </ShareDetailArea>
      ) : (
        <>
          <InputAreaRight data-testid="project_select" workflowFormWidth={workflowFormWidth}>
            <SelectProjects
              placeholder="プロジェクト(案件)を検索"
              options={reserveInfo.projects.map(p => {
                return { label: p.codeAndName, value: p.id?.toString() || '', matcher: p.codeAndName };
              })}
              selected={reserveInfo.projectOption}
              onSelect={onSelect}
              isRequired={reserveInfo.isProjectNameRequired}
              workflowFormWidth={workflowFormWidth}
            />
          </InputAreaRight>
          {reserveInfo.projectShareAvailability && (
            <DetailLinkArea>
              <A
                onClick={() => {
                  reserveInfo.showProjectDetail = true;
                  reserveInfo.initOrderItemMappings('project');
                  setShowDetail(true);
                }}
              >
                詳細設定
              </A>
            </DetailLinkArea>
          )}
        </>
      )}
    </>
  );
};

const Label = styled.div`
  font-size: 13px;
  width: 200px;
  line-height: 34px;
`;

const InputAreaRight = styled.div<{ workflowFormWidth?: string }>`
  line-height: 34px;
  display: flex;
  width: ${props => props.workflowFormWidth || '420px'};
`;
const DetailLinkArea = styled.div`
  font-weight: bold;
  margin-top: 5px;
  margin-left: 8px;
`;

const ShareDetailModeLinkArea = styled.div`
  font-weight: bold;
  margin-top: 8px;
  margin-right: 16px;
`;

const ShareDetailModeSelected = styled.p`
  font-weight: bold;
  margin-top: 8px;
  margin-right: 16px;
`;

const ShareDetailArea = styled.div<{ workflowFormWidth?: string }>`
  display: block;
  margin-bottom: 24px;
  width: ${props => props.workflowFormWidth || '420px'};
`;

const ShareDetailFlexArea = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

const MappingDetailLabel = styled.p`
  font-weight: bold;
  margin-top: 4px;
  margin-left: 8px;
`;

const ShareDetailLabel = styled.p`
  font-weight: bold;
  margin-top: 4px;
  margin-left: 8px;
  width: 60px;
`;

const ShareDetailIndentedArea = styled.div`
  margin-top: 8px;
  margin-left: 16px;
  width: ${DETAIL_FORM_WIDTH};
`;

const ShareDetailSelectArea = styled.div`
  width: ${DETAIL_FORM_WIDTH};
`;

const Red = styled.span`
  color: ${props => props.theme.redColor};
`;

export default observer(ProjectNameSection);
